/**
 * Push reminder clock policy, shared by the editor and its validation.
 *
 * Stored clocks are `{ hours, minutes, period }`. The editor has always saved
 * converted UTC values (`period: 'UTC'`); untouched legacy rows may still hold
 * the old 12-hour default (`12:00 AM`), which the API has always read as UTC.
 * Nothing here reinterprets a saved value using the browser timezone.
 */
export const MAX_PUSH_REMINDER_TIMES = 24;

const isDigits = (value) => /^\d{1,2}$/.test(String(value ?? ''));

/** Normalize a stored clock to 24-hour UTC `{ hours, minutes }`, or null when malformed. */
export function toUtcClock(time) {
  if (!time || !isDigits(time.hours) || !isDigits(time.minutes)) return null;
  let hours = Number(time.hours);
  const minutes = Number(time.minutes);
  if (minutes > 59) return null;
  if (time.period === 'AM' || time.period === 'PM') {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (time.period === 'PM' ? 12 : 0);
  } else if (time.period === 'UTC' || time.period === '24H') {
    if (hours > 23) return null;
  } else {
    return null;
  }
  return { hours, minutes };
}

/** Return a user-facing error, or null when the schedule's push settings are valid. */
export function pushReminderError(schedule) {
  if (schedule.enablePushReminders !== true) return null;
  const times = schedule.pushReminderTimers ?? [];
  if (!Array.isArray(times) || times.length > MAX_PUSH_REMINDER_TIMES) {
    return `Use at most ${MAX_PUSH_REMINDER_TIMES} push reminder times.`;
  }
  const seen = new Set();
  for (const time of times) {
    const clock = toUtcClock(time);
    if (!clock) return 'Enter a valid push reminder time.';
    const key = `${clock.hours}:${clock.minutes}`;
    if (seen.has(key)) return 'Remove duplicate push reminder times.';
    seen.add(key);
  }
  return null;
}
